/* feels like mahjong, theme behaviour (v0.3). No framework, no dependencies.
   Every component works without JS; this layer adds the header states, the
   bag drawer, the product gallery and the product page helpers. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const debounce = (fn, ms) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

/* Header: paper, always; hides while reading down and returns on the way up.
   It only changes after the page has travelled 12 px in one direction, so a
   trackpad's small reversals and momentum tails do not make it flicker. */
const HEADER_HIDE_AFTER = 320;
const HEADER_TRAVEL = 12;

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.closest('.section-header') || this;
    this.anchor = window.scrollY;
    // Whether the bar is tucked away (its own flag: `hidden` is the DOM's and would hide the element).
    this.tucked = false;
    this.queued = false;
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }
  onScroll() {
    if (this.queued) return;
    this.queued = true;
    requestAnimationFrame(() => this.update());
  }
  update() {
    this.queued = false;
    const y = window.scrollY;
    const locked = document.body.classList.contains('is-locked');
    let hide = this.tucked;
    if (y < HEADER_HIDE_AFTER || locked) hide = false;
    else if (y - this.anchor > HEADER_TRAVEL) hide = true;
    else if (this.anchor - y > HEADER_TRAVEL) hide = false;
    // The anchor follows the page in the current direction, so travel is measured from the last turn.
    this.wrapper.classList.toggle('is-scrolled', y > 8);
    if ((hide && y > this.anchor) || (!hide && y < this.anchor) || hide !== this.tucked) this.anchor = y;
    if (hide !== this.tucked) {
      this.tucked = hide;
      this.wrapper.classList.toggle('is-hidden', hide);
    }
  }
}
customElements.define('site-header', SiteHeader);

/* Drawers and sheets share one pattern: open, trap focus, close on Escape
   or on the overlay, return focus to the trigger. */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

class FlmPanel extends HTMLElement {
  connectedCallback() {
    this.overlay = document.getElementById(this.dataset.overlay);
    this.onKey = this.onKey.bind(this);
    this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.close()));
    this.overlay?.addEventListener('click', () => this.close());
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((t) =>
      t.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(t);
      })
    );
  }
  open(trigger) {
    this.trigger = trigger;
    this.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'true'));
    document.body.classList.add('is-locked');
    document.addEventListener('keydown', this.onKey);
    const first = this.querySelector('[data-autofocus]') || this.querySelector(FOCUSABLE);
    requestAnimationFrame(() => first?.focus());
  }
  close() {
    this.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('is-locked');
    document.removeEventListener('keydown', this.onKey);
    this.trigger?.focus();
  }
  onKey(e) {
    if (e.key === 'Escape') this.close();
    if (e.key !== 'Tab') return;
    const items = [...this.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}
customElements.define('flm-panel', FlmPanel);

/* Bag: add forms post to /cart/add.js, then the drawer section is re-rendered
   through the Section Rendering API so its markup stays in Liquid. */
async function refreshCart() {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer) return;
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`);
  if (!res.ok) throw new Error('The cart could not be refreshed. Reload the page to see it.');
  const html = new DOMParser().parseFromString(await res.text(), 'text/html');
  const fresh = html.querySelector('#CartDrawerInner');
  if (fresh) drawer.querySelector('#CartDrawerInner').replaceWith(fresh);
  const count = html.querySelector('[data-cart-count]')?.textContent;
  if (count != null) document.querySelectorAll('[data-cart-count]').forEach((el) => (el.textContent = count.trim()));
}

document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[data-add-to-cart]');
  if (!form) return;
  e.preventDefault();
  // Bundles first (Theme settings > Launch): a held product never reaches the bag.
  if (form.dataset.held === 'held' || form.dataset.held === 'browse') return;
  const buttons = [...document.querySelectorAll(`[type="submit"][form="${form.id}"]`), ...form.querySelectorAll('[type="submit"]')];
  const main = form.querySelector('[type="submit"]');
  // innerHTML, not text: the PDP button carries the price in its own span, which a variant change updates.
  const label = main?.innerHTML;
  const msg = form.querySelector('[data-cart-error]');
  if (msg) msg.textContent = '';
  buttons.forEach((b) => (b.disabled = true));
  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });
    if (!res.ok) {
      throw new Error(res.status === 422
        ? 'There are no more of these to add. Refresh the page to see what is available now.'
        : 'This could not be added to your cart. Please try again.');
    }
    if (main) main.textContent = main.dataset.added || 'Added';
    // The cart page lists its lines in Liquid, so an add from its "Goes with it" reloads it.
    if (form.classList.contains('addon__form--reload')) { window.location.reload(); return; }
    await refreshCart();
    document.getElementById('CartDrawer')?.open(main);
  } catch (err) {
    console.error('[add to bag]', err);
    if (msg) msg.textContent = err instanceof TypeError ? 'The connection dropped. Please try again.' : err.message;
  } finally {
    buttons.forEach((b) => (b.disabled = false));
    if (main) setTimeout(() => {
      main.innerHTML = label;
      const price = main.querySelector('[data-price]');
      if (form.dataset.currentPrice && price) price.textContent = form.dataset.currentPrice;
    }, 1600);
  }
});

/* Before checkout: the first press of Check out, in the drawer or on the cart page, when the bag has pieces
   that go with it (snippets/cart-upsell: racks, mats, the tile set and accessories it is missing), brings that
   list into view as "Before you check out" and the button reads Continue to checkout; the next press checks
   out. Once per visit (the cart page reloads after every change), and never when the list is empty. Without
   script Check out goes straight through. */
const NUDGED = 'flm-checkout-nudged';
let checkoutNudged = false;
try { checkoutNudged = sessionStorage.getItem(NUDGED) === '1'; } catch (err) { /* storage blocked: once per page */ }
/* Bag lines whose tile shipment sold out after they were added (snippets/cart-line marks them with
   data-ship-fix): the press of Check out first moves each to the batch on sale now through
   /cart/change.js, then checks out as before. Runs ahead of the nudges below. */
let shipFixed = false;
document.addEventListener('submit', async (e) => {
  const btn = e.submitter;
  if (shipFixed || !btn || !btn.matches('[data-checkout]')) return;
  const scope = btn.closest('#CartDrawer') || btn.closest('.cart-page') || document;
  const lines = [...scope.querySelectorAll('[data-ship-fix]')];
  if (!lines.length) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  const form = e.target;
  btn.disabled = true;
  try {
    for (const li of lines) {
      // Liquid prints line properties as [name, value] pairs.
      const raw = JSON.parse(li.dataset.props || '{}');
      const properties = Array.isArray(raw) ? Object.fromEntries(raw) : raw;
      properties[li.dataset.shipKey] = li.dataset.shipNow;
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: li.dataset.key, quantity: Number(li.dataset.qtyNow || 1), properties }),
      });
      if (!res.ok) throw new Error(`cart/change ${res.status}`);
      li.removeAttribute('data-ship-fix');
    }
  } catch (err) {
    console.error('[tiles ship]', err);
  } finally {
    btn.disabled = false;
    shipFixed = true;
    form.requestSubmit(btn);
  }
}, true);

/* Over the free tote amount with no tote picked, the first press of Check out
   brings the tote choice into view instead (once per visit, its own key, so a
   bag that crosses the amount later is still asked). */
const GIFT_NUDGED = 'flm-gift-nudged';
let giftNudged = false;
try { giftNudged = sessionStorage.getItem(GIFT_NUDGED) === '1'; } catch (err) { /* storage blocked: once per page */ }
document.addEventListener('submit', (e) => {
  const btn = e.submitter;
  if (giftNudged || !btn || !btn.matches('[data-checkout]')) return;
  const scope = btn.closest('#CartDrawer') || btn.closest('.cart-page');
  const gift = scope?.querySelector('[data-gift="ready"]');
  if (!gift || !gift.querySelector('.addon')) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  giftNudged = true;
  checkoutNudged = true;
  try { sessionStorage.setItem(GIFT_NUDGED, '1'); sessionStorage.setItem(NUDGED, '1'); } catch (err) { /* storage blocked: once per page */ }
  const title = gift.querySelector('.gift__title');
  if (title && gift.dataset.nudge) title.textContent = gift.dataset.nudge;
  gift.classList.add('gift--nudge');
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  gift.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
  gift.querySelector('button, a.btn')?.focus({ preventScroll: true });
  if (btn.dataset.continue) btn.textContent = btn.dataset.continue;
}, true);

document.addEventListener('submit', (e) => {
  const btn = e.submitter;
  if (e.defaultPrevented || checkoutNudged || !btn || !btn.matches('[data-checkout]')) return;
  const scope = btn.closest('#CartDrawer') || btn.closest('.cart-page');
  const list = scope?.querySelector('.addons');
  if (!list || !list.querySelector('.addon')) return;
  e.preventDefault();
  checkoutNudged = true;
  try { sessionStorage.setItem(NUDGED, '1'); } catch (err) { /* storage blocked: once per page */ }
  const title = list.querySelector('.addons__title');
  const note = list.querySelector('[data-addons-note]');
  const missingTitle = list.dataset.missingTitle || btn.dataset.beforeTitle;
  const missingNote = list.dataset.missingNote || btn.dataset.beforeNote;
  if (title && missingTitle) title.textContent = missingTitle;
  if (note && missingNote) note.textContent = missingNote;
  list.classList.add('addons--nudge');
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  list.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
  if (btn.dataset.continue) btn.textContent = btn.dataset.continue;
});

/* Bag lines: the drawer's steppers and every Remove link post to
   /cart/change.js. In the drawer the bag is re-rendered in place and focus
   returns to the same control (or the heading when the line is gone); on the
   cart page the page reloads. A failure is shown under the lines. */
document.addEventListener('click', async (e) => {
  const control = e.target.closest('[data-line-qty], [data-line-remove]');
  if (!control) return;
  e.preventDefault();
  if (control.getAttribute('aria-disabled') === 'true') return;
  const drawer = control.closest('#CartDrawer');
  const line = Number(control.dataset.line);
  const now = Number(control.closest('[data-line-item]')?.dataset.qtyNow || 1);
  const quantity = control.hasAttribute('data-line-remove') ? 0 : Math.max(0, now + Number(control.dataset.lineQty));
  const msg = (drawer || document).querySelector('[data-cart-msg]');
  if (msg) msg.textContent = '';
  control.setAttribute('aria-disabled', 'true');
  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    if (!res.ok) {
      throw new Error(res.status === 422
        ? 'There are no more of these to add.'
        : 'The cart could not be changed. Please try again.');
    }
    if (!drawer) { window.location.reload(); return; }
    const key = control.hasAttribute('data-line-remove') ? '[data-line-remove]' : `[data-line-qty="${control.dataset.lineQty}"]`;
    await refreshCart();
    const status = drawer.querySelector('[data-cart-status]');
    if (status) status.textContent = status.dataset.updated || 'Cart updated.';
    const again = quantity > 0 && drawer.querySelector(`[data-line="${line}"]${key}`);
    (again || drawer.querySelector('#CartDrawerTitle'))?.focus?.({ preventScroll: true });
  } catch (err) {
    console.error('[bag]', err);
    if (msg) msg.textContent = err instanceof TypeError ? 'The connection dropped. Please try again.' : err.message;
  } finally {
    control.removeAttribute('aria-disabled');
  }
});

/* Cart page quantities: a changed number posts the form through its Update
   button, the same request that button makes, so the lines and subtotal match
   the bag without a second press. Enter already did this; leaving the box now
   does too. An empty box waits for a number. */
document.addEventListener('change', (e) => {
  const input = e.target.closest('.cart-page__form .cart-line__input');
  if (!input || input.value === '') return;
  const update = input.form.querySelector('.cart-page__update');
  if (update) input.form.requestSubmit(update);
});

/* Product gallery. Wide screens: the photographs are a stack in the page;
   the rail and the counter follow whichever one is in view, and a thumbnail
   scrolls its photograph into place. Under 1000px: a swipe strip (native
   scroll snap) with a counter and a progress line. Any photograph opens the
   lightbox, which loads the largest file. The lightbox is a <dialog>, so the
   browser traps focus and Escape closes it; focus returns to the opener. */
class FlmGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    if (!this.slides.length) return;
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.strip = this.querySelector('[data-slides]');
    this.indexEls = [...this.querySelectorAll('[data-index]')];
    this.progress = this.querySelector('[data-progress]');
    this.box = this.querySelector('[data-lightbox]');
    this.i = 0;
    this.narrow = window.matchMedia('(max-width: 999px)');

    this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb), true)));
    this.strip?.addEventListener('keydown', (e) => {
      if (!this.strip.isSameNode(e.target)) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); this.go(this.i + 1, true); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); this.go(this.i - 1, true); }
    });
    this.strip?.addEventListener('scroll', debounce(() => this.onSwipe(), 60), { passive: true });
    this.querySelectorAll('[data-open]').forEach((b) => b.addEventListener('click', () => {
      this.go(this.slides.indexOf(b.closest('[data-slide]')), false);
      this.openBox(b);
    }));
    if ('IntersectionObserver' in window) {
      // A thin band a third of the way down the screen decides which photograph is current.
      this.seen = new IntersectionObserver((entries) => {
        if (this.narrow.matches || this.jumping) return;
        const hit = entries.filter((e) => e.isIntersecting).map((e) => this.slides.indexOf(e.target));
        if (hit.length) this.go(Math.min(...hit), false);
      }, { rootMargin: '-33% 0px -62% 0px' });
      this.slides.forEach((s) => this.seen.observe(s));
    }
    if (this.box) this.setupBox();
    this.go(0, false);
  }

  disconnectedCallback() {
    this.seen?.disconnect();
  }

  go(n, scroll) {
    const count = this.slides.length;
    this.i = (n + count) % count;
    this.slides.forEach((s, k) => s.classList.toggle('is-current', k === this.i));
    this.thumbs.forEach((t, k) => (k === this.i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
    this.indexEls.forEach((el) => (el.textContent = String(this.i + 1)));
    this.progress?.style.setProperty('--i', String(this.i));
    const cur = this.thumbs[this.i];
    const row = cur?.closest('.pdp__rail-wrap');
    if (this.narrow.matches && row && row.scrollWidth > row.clientWidth) {
      // Keep the current thumbnail in view in the phone row, without moving the page.
      const left = row.scrollLeft + cur.getBoundingClientRect().left - row.getBoundingClientRect().left - row.clientWidth / 2 + cur.offsetWidth / 2;
      row.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    if (!scroll) return;
    const behavior = reduceMotion.matches ? 'auto' : 'smooth';
    if (this.narrow.matches) {
      this.strip.scrollTo({ left: this.slides[this.i].offsetLeft - this.strip.offsetLeft, behavior });
    } else {
      // Hold the observer while the page travels, so the rail does not flicker through every photograph.
      this.jumping = true;
      clearTimeout(this.jumpTimer);
      this.jumpTimer = setTimeout(() => (this.jumping = false), reduceMotion.matches ? 50 : 700);
      this.slides[this.i].scrollIntoView({ block: 'start', behavior });
    }
  }

  onSwipe() {
    if (!this.narrow.matches) return;
    const best = Math.round(this.strip.scrollLeft / Math.max(1, this.strip.clientWidth));
    if (best !== this.i && this.slides[best]) this.go(best, false);
  }

  setupBox() {
    const b = this.box;
    this.lb = {
      img: b.querySelector('[data-lb-img]'),
      view: b.querySelector('[data-lb-view]'),
      index: b.querySelector('[data-lb-index]'),
      hint: b.querySelector('[data-lb-hint]'),
      thumbs: b.querySelector('[data-lb-thumbs]'),
    };
    this.thumbs.forEach((t, k) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('aria-label', t.getAttribute('aria-label'));
      btn.appendChild(t.querySelector('img').cloneNode());
      btn.addEventListener('click', () => this.show(k));
      li.appendChild(btn);
      this.lb.thumbs.appendChild(li);
    });
    b.querySelector('[data-lb-close]').addEventListener('click', () => b.close());
    b.querySelector('[data-lb-prev]').addEventListener('click', () => this.show(this.i - 1));
    b.querySelector('[data-lb-next]').addEventListener('click', () => this.show(this.i + 1));
    b.addEventListener('close', () => {
      document.body.classList.remove('is-locked');
      this.opener?.focus();
    });
    b.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.show(this.i + 1);
      if (e.key === 'ArrowLeft') this.show(this.i - 1);
      if (e.key !== 'Tab') return;
      // The browser lets Tab leave a modal dialog for its own toolbar; keep it inside.
      const items = [...b.querySelectorAll('button')].filter((el) => el.offsetParent !== null);
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    // Zoom: double click or double tap toggles the full file at native size.
    this.lb.img.addEventListener('dblclick', (e) => this.zoom(e));
    let lastTap = 0;
    this.lb.img.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastTap < 300) { e.preventDefault(); this.zoom(e.changedTouches[0]); }
      lastTap = now;
    });
    // Mouse drag pans a zoomed plate; touch pans by native scroll.
    let drag = null;
    this.lb.view.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || !this.lb.view.classList.contains('is-zoomed')) return;
      drag = { x: e.clientX, y: e.clientY, l: this.lb.view.scrollLeft, t: this.lb.view.scrollTop };
      this.lb.view.classList.add('is-dragging');
      this.lb.view.setPointerCapture(e.pointerId);
    });
    this.lb.view.addEventListener('pointermove', (e) => {
      if (!drag) return;
      this.lb.view.scrollLeft = drag.l - (e.clientX - drag.x);
      this.lb.view.scrollTop = drag.t - (e.clientY - drag.y);
    });
    const end = () => { drag = null; this.lb.view.classList.remove('is-dragging'); };
    this.lb.view.addEventListener('pointerup', end);
    this.lb.view.addEventListener('pointercancel', end);
  }

  openBox(opener) {
    if (!this.box || typeof this.box.showModal !== 'function') return;
    this.opener = opener;
    document.body.classList.add('is-locked');
    this.box.showModal();
    this.show(this.i);
    if (this.lb.hint) {
      this.lb.hint.hidden = false;
      this.lb.hint.style.opacity = '1';
      clearTimeout(this.hintTimer);
      this.hintTimer = setTimeout(() => {
        this.lb.hint.style.opacity = '0';
        setTimeout(() => (this.lb.hint.hidden = true), 400);
      }, 3000);
    }
  }

  show(n) {
    this.go(n, false);
    const slide = this.slides[this.i];
    const { img, view, index, thumbs } = this.lb;
    view.classList.remove('is-zoomed');
    img.src = slide.dataset.full;
    img.alt = slide.dataset.alt || '';
    if (index) index.textContent = String(this.i + 1);
    [...thumbs.querySelectorAll('button')].forEach((t, k) => (k === this.i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
  }

  zoom(point) {
    const { view, img } = this.lb;
    const zoomed = view.classList.toggle('is-zoomed');
    if (!zoomed) return;
    const r = img.getBoundingClientRect();
    const fx = (point.clientX - r.left) / r.width;
    const fy = (point.clientY - r.top) / r.height;
    requestAnimationFrame(() => {
      view.scrollLeft = img.offsetWidth * fx - view.clientWidth / 2;
      view.scrollTop = img.offsetHeight * fy - view.clientHeight / 2;
    });
  }
}
customElements.define('flm-gallery', FlmGallery);

/* Product page: gallery position on phones, quantity, variant, buy bar. */
function initProduct() {
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty]');
    if (!step) return;
    const input = step.parentElement.querySelector('input');
    const next = Math.max(Number(input.min) || 1, (Number(input.value) || 1) + Number(step.dataset.qty));
    input.value = String(next);
  });

  /* Options: each change finds the variant whose values match every group,
     then updates the hidden id, prices, the button, the address and the
     gallery. A missing combination disables the button and says so. */
  document.querySelectorAll('[data-options]').forEach((box) => {
    const form = box.closest('form');
    const section = form.closest('.pdp');
    const variants = JSON.parse(box.parentElement.querySelector('[data-variants]')?.textContent || '[]');
    const add = form.querySelector('[data-main-add]');
    const none = box.querySelector('[data-variant-none]');
    const gallery = section?.querySelector('flm-gallery');
    box.addEventListener('change', () => {
      const picked = [...box.querySelectorAll('fieldset')].map((f) => f.querySelector('input:checked')?.value);
      picked.forEach((v, i) => { const el = box.querySelector(`[data-picked="${i}"]`); if (el) el.textContent = v || ''; });
      const variant = variants.find((v) => v.options.every((o, i) => o === picked[i]));
      none.hidden = Boolean(variant);
      if (!variant) { if (add) add.disabled = true; return; }
      form.querySelector('[data-variant-id]').value = String(variant.id);
      form.dataset.currentPrice = variant.price;
      section?.querySelectorAll('[data-price]').forEach((el) => (el.textContent = variant.price));
      // A shipping-window option: the line under the button and the FAQ name the picked window.
      section?.querySelectorAll('[data-ship-window]').forEach((el) => { if (el.dataset.template && variant.title) el.textContent = el.dataset.template.replace('%s', variant.title); });
      if (add) {
        const held = form.dataset.held === 'held' || form.dataset.held === 'browse';
        add.disabled = held || !variant.available;
        const label = add.querySelector('[data-add-label]');
        if (label) label.textContent = held ? add.dataset.heldLabel : !variant.available ? add.dataset.soldLabel : variant.pre ? add.dataset.preLabel : add.dataset.addLabel;
        // The pre-order note goes on the order only for a variant that is on pre-order.
        const preProp = box.closest('form')?.querySelector('[data-pre-prop]');
        if (preProp) preProp.disabled = !variant.pre;
        // A mat, rack or bag line reads as a pre-order once the picked variant has sold out.
        const goods = box.closest('form')?.querySelector('[data-goods-ship]');
        if (goods) goods.value = variant.pre ? goods.dataset.pre : goods.dataset.now;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
      if (variant.media && gallery?.slides) {
        const n = gallery.slides.findIndex((s) => s.dataset.mediaId === String(variant.media));
        if (n > -1) gallery.go(n, true);
      }
    });
  });

  /* Sticky bar: shown once the main Add to bag has scrolled above the screen,
     hidden again when the footer arrives so it never covers it. A scroll
     check rather than an observer, so it is exact on every frame. */
  const bar = document.querySelector('[data-buy-bar]');
  const add = document.querySelector('[data-main-add]');
  const footer = document.querySelector('.site-footer');
  if (bar && add) {
    /* A held product's bar carries a Notify me link rather than a button. */
    const barControls = [...bar.querySelectorAll('button, a[href]')];
    let queued = false;
    const update = () => {
      queued = false;
      const past = add.getBoundingClientRect().bottom < 0;
      const atFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const show = past && !atFooter;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      barControls.forEach((el) => { el.tabIndex = show ? 0 : -1; });
    };
    const queue = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
  }
}

/* Pick a tile (home, scene 05): a list of toggle buttons, one frame each.
   Up and down arrows move between tiles, as in a list. Without script every
   frame shows in a column, so nothing depends on this. */
function initPick(root = document) {
  root.querySelectorAll('[data-pick]').forEach((pick) => {
    const tabs = [...pick.querySelectorAll('[data-pick-tab]')];
    const frames = [...pick.querySelectorAll('[data-pick-frame]')];
    const show = (i) => {
      tabs.forEach((t, n) => t.setAttribute('aria-pressed', String(n === i)));
      frames.forEach((f, n) => f.classList.toggle('is-current', n === i));
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => show(i));
      tab.addEventListener('keydown', (e) => {
        const d = { ArrowDown: 1, ArrowUp: -1 }[e.key];
        if (!d) return;
        e.preventDefault();
        const next = (i + d + tabs.length) % tabs.length;
        tabs[next].focus();
        show(next);
      });
    });
  });
}

/* Forms (sign-up, contact): the browser's own checks are kept for the no-script
   case; with script, each field says in words what is wrong, under itself, and
   the first wrong field takes focus. Offline is caught before the page posts.
   After a post, Shopify returns to the page with the thank-you or the error in
   place; that message takes focus so it is read out and seen. */
function initForms(root = document) {
  const words = window.flmStrings || {};
  root.querySelectorAll('form[data-validate]').forEach((form) => {
    form.noValidate = true;
    const fields = [...form.querySelectorAll('input[required], textarea[required], input[type="email"]')];
    const errorFor = (field) => form.querySelector(`#${field.id}-error`);
    const check = (field) => {
      const v = field.validity;
      let msg = '';
      if (v.valueMissing) msg = words.required || 'Please fill this in.';
      else if (v.typeMismatch || (field.type === 'email' && field.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim()))) msg = words.email || 'Please enter a full email address.';
      const out = errorFor(field);
      if (out) out.textContent = msg;
      if (msg) field.setAttribute('aria-invalid', 'true'); else field.removeAttribute('aria-invalid');
      return !msg;
    };
    fields.forEach((field) => {
      field.addEventListener('blur', () => { if (field.value) check(field); });
      field.addEventListener('input', () => { if (field.getAttribute('aria-invalid') === 'true') check(field); });
    });
    form.addEventListener('submit', (e) => {
      const general = form.querySelector('[data-form-error]');
      if (general) general.textContent = '';
      const bad = fields.filter((f) => !check(f));
      if (bad.length) {
        e.preventDefault();
        bad[0].focus();
        return;
      }
      if (navigator.onLine === false) {
        e.preventDefault();
        const out = general || errorFor(fields[0]);
        if (out) out.textContent = words.offline || 'You seem to be offline. Check your connection and try again.';
        return;
      }
      const button = form.querySelector('[type="submit"]');
      if (button) { button.setAttribute('aria-busy', 'true'); button.dataset.label = button.innerHTML; button.textContent = words.sending || 'Sending'; }
    });
  });
  const status = root.querySelector('[data-form-status]');
  if (status) {
    status.scrollIntoView({ block: 'center' });
    status.focus({ preventScroll: true });
    // Chrome drops focus to the body when it scrolls to the #signup fragment after load; put it back.
    const settle = () => { if (!document.activeElement || document.activeElement === document.body) status.focus({ preventScroll: true }); };
    window.addEventListener('load', () => requestAnimationFrame(settle), { once: true });
    setTimeout(settle, 400);
    setTimeout(settle, 1200);
  }
}

/* Text and email sign-up: both fields on screen, one or both filled in. A
   number alone is enough only when Klaviyo is set on the form; otherwise it
   needs an email with it so Shopify can keep it. */
function usDigits(value) {
  let d = String(value || '').replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') d = d.slice(1);
  return d;
}
function formatUs(d) {
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)} ${d.slice(6, 10)}`;
}
function initTexts(root = document) {
  const words = window.flmStrings || {};
  const remember = 'flm-texts';
  root.querySelectorAll('form[data-texts]').forEach((form) => {
    const phone = form.querySelector('#TextsPhone');
    const email = form.querySelector('#TextsEmail');
    const firstName = form.querySelector('#TextsFirstName');
    if (form.querySelector('[data-form-status]')) {
      try { sessionStorage.removeItem(remember); } catch (err) { /* nothing was stored */ }
    }
    if (!phone || !email) return;
    form.noValidate = true;
    const general = form.querySelector('[data-form-error]');
    const tag = form.querySelector('[data-texts-tag]');
    const key = form.dataset.klaviyoKey;
    const list = form.dataset.klaviyoList;
    // A number alone can only be kept by an SMS app; without one it needs an email with it.
    const smsReady = form.dataset.smsReady === 'true' && !!key && !!list;

    const say = (field, msg) => {
      const out = form.querySelector(`#${field.id}-error`);
      if (out) out.textContent = msg;
      if (msg) field.setAttribute('aria-invalid', 'true'); else field.removeAttribute('aria-invalid');
      return !msg;
    };
    const checkPhone = () => {
      const d = usDigits(phone.value);
      if (!d) return say(phone, '');
      if (d.length !== 10 || /^[01]/.test(d)) return say(phone, words.phoneInvalid || 'Please enter a 10 digit US mobile number.');
      return say(phone, '');
    };
    const checkEmail = () => {
      const v = email.value.trim();
      if (!v) return say(email, '');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return say(email, words.email || 'Please enter a full email address.');
      return say(email, '');
    };

    // Shopify sent the form back with an error: put the number back.
    if (form.querySelector('#TextsEmail[aria-invalid]')) {
      let saved = '';
      try { saved = sessionStorage.getItem(remember) || ''; } catch (err) { saved = ''; }
      if (saved) phone.value = formatUs(usDigits(saved));
    }

    phone.addEventListener('input', () => {
      const d = usDigits(phone.value);
      const end = phone.selectionStart === phone.value.length;
      phone.value = formatUs(d.slice(0, 10));
      if (end) phone.setSelectionRange(phone.value.length, phone.value.length);
      if (phone.getAttribute('aria-invalid') === 'true') checkPhone();
    });
    phone.addEventListener('blur', () => { if (phone.value) checkPhone(); });
    email.addEventListener('input', () => { if (email.getAttribute('aria-invalid') === 'true') checkEmail(); });
    email.addEventListener('blur', () => { if (email.value) checkEmail(); });

    const thankInPlace = () => {
      const ok = document.createElement('div');
      ok.className = 'form-status form-status--ok texts__thanks';
      ok.setAttribute('role', 'status');
      ok.tabIndex = -1;
      const p = document.createElement('p');
      p.textContent = form.dataset.success || '';
      ok.append(p);
      form.replaceChildren(ok);
      ok.focus({ preventScroll: true });
      try { sessionStorage.removeItem(remember); } catch (err) { /* nothing was stored */ }
    };
    const klaviyo = (attributes) => fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(key)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
      body: JSON.stringify({ data: {
        type: 'subscription',
        attributes: { custom_source: form.dataset.source || 'Text sign-up page', profile: { data: { type: 'profile', attributes } } },
        relationships: { list: { data: { type: 'list', id: list } } },
      } }),
    }).then((res) => { if (!res.ok) throw new Error(`Klaviyo ${res.status}`); });

    let sending = false;
    form.addEventListener('submit', async (e) => {
      if (general) general.textContent = '';
      const hasPhone = !!usDigits(phone.value);
      const hasEmail = !!email.value.trim();
      const stop = (field, msg) => { e.preventDefault(); if (msg !== undefined) say(field, msg); field.focus(); };
      if (!hasEmail) { stop(email, words.emailRequired || 'Please enter your email address.'); return; }
      if (hasPhone && !checkPhone()) { stop(phone); return; }
      if (hasEmail && !checkEmail()) { stop(email); return; }
      if (hasPhone && !hasEmail && !smsReady) { stop(email, words.needEmail || 'Please add your email too, so we can keep your number.'); return; }
      if (navigator.onLine === false) {
        e.preventDefault();
        if (general) general.textContent = words.offline || 'You seem to be offline. Check your connection and try again.';
        return;
      }
      const e164 = hasPhone ? `+1${usDigits(phone.value)}` : '';
      try { sessionStorage.setItem(remember, e164); } catch (err) { /* private mode: the number is asked again */ }
      if (tag) tag.value = hasPhone ? (form.dataset.tagSms || 'sms-signup') : (form.dataset.tagEmail || 'email-signup');
      phone.value = e164;
      phone.disabled = !hasPhone; // an empty number is not sent
      const button = form.querySelector('[type="submit"]');
      const busy = () => {
        if (!button) return;
        button.setAttribute('aria-busy', 'true');
        button.dataset.label = button.innerHTML;
        button.textContent = words.sending || 'Sending';
      };
      const undo = () => {
        phone.disabled = false;
        phone.value = formatUs(usDigits(e164));
        if (button && button.dataset.label) { button.removeAttribute('aria-busy'); button.innerHTML = button.dataset.label; }
      };

      if (!smsReady) { busy(); return; } // the Shopify form sends itself
      e.preventDefault();
      if (sending) return;
      sending = true;
      busy();
      const subscriptions = {};
      const attributes = { subscriptions };
      if (hasPhone) { attributes.phone_number = e164; subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } }; }
      if (hasEmail) { attributes.email = email.value.trim(); subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } }; }
      if (firstName && firstName.value.trim()) attributes.first_name = firstName.value.trim();
      try {
        await klaviyo(attributes);
        if (hasEmail) form.submit(); else thankInPlace();
      } catch (err) {
        console.error(err);
        // Klaviyo did not take it: an email can still be kept by the Shopify form.
        if (hasEmail) { form.submit(); return; }
        sending = false;
        undo();
        if (general) general.textContent = words.notSaved || 'We could not save that just now. Please try again.';
      }
    });
  });
}

/* A page restored from the back-forward cache keeps a pressed Send button; put its label back. */
window.addEventListener('pageshow', () => {
  document.querySelectorAll('form[data-validate] [aria-busy="true"]').forEach((b) => {
    b.removeAttribute('aria-busy');
    if (b.dataset.label) b.innerHTML = b.dataset.label;
  });
});

/* Cookie consent (snippets/cookie-banner). Shopify's own banner wins when the
   store has it on (window.privacyBanner). Otherwise this one asks, only where
   Shopify's Customer Privacy API says consent is needed, and records the
   answer through that API so checkout and apps read the same choice. */
function initCookies() {
  const box = document.querySelector('[data-cookies]');
  const openers = document.querySelectorAll('[data-cookie-prefs]');
  const show = () => { if (!box) return; box.hidden = false; box.querySelector('[data-cookies-accept]')?.focus({ preventScroll: true }); };
  const record = (yes) => {
    const api = window.Shopify?.customerPrivacy;
    const done = () => { if (box) box.hidden = true; };
    if (!api?.setTrackingConsent) { done(); return; }
    api.setTrackingConsent({ analytics: yes, marketing: yes, preferences: yes, sale_of_data: yes }, (err) => {
      if (err) console.error('[cookies]', err);
      done();
    });
  };
  openers.forEach((a) => a.addEventListener('click', (e) => {
    e.preventDefault();
    if (window.privacyBanner?.showPreferences) window.privacyBanner.showPreferences();
    else show();
  }));
  if (!box) return;
  box.querySelector('[data-cookies-accept]')?.addEventListener('click', () => record(true));
  box.querySelector('[data-cookies-decline]')?.addEventListener('click', () => record(false));
  if (window.privacyBanner) return;
  if (typeof window.Shopify?.loadFeatures !== 'function') return;
  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
    if (err) { console.error('[cookies]', err); return; }
    const api = window.Shopify.customerPrivacy;
    if (window.privacyBanner) return;
    if (api?.shouldShowBanner?.()) show();
  });
}

// Bundles first with a launch time (Theme settings > Launch): a page that holds single pieces back reloads
// itself the moment they open, so nobody has to refresh. The time comes from the store's own clock (the Date
// header of /cart.js), so a phone whose clock is off neither reloads early nor late. A reload that still lands
// on a held page (a cached copy) tries again with a fresh address, waiting longer each time, up to eight times.
// A tab that slept through the opening checks again when it is shown.
function initOpening() {
  const meta = document.querySelector('meta[name="flm-opens-at"]');
  const at = Number(meta?.content) * 1000;
  // Before the launch time every page is in the preview (no cart), so every page reloads when it opens.
  const preview = meta?.dataset.phase === 'pre';
  if (!at || (!preview && !document.querySelector('[data-held="held"], .card__held, .held'))) return;
  const tries = Number(new URL(window.location.href).searchParams.get('opened-try') || 0);
  let timer = 0;
  const reopen = () => {
    if (tries >= 8) return;
    const url = new URL(window.location.href);
    url.searchParams.set('opened', String(at / 1000));
    url.searchParams.set('opened-try', String(tries + 1));
    window.location.replace(url.toString());
  };
  const storeNow = async () => {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
      const date = Date.parse(res.headers.get('date') || '');
      if (date) return date;
    } catch (err) {
      console.warn('[opening] store clock unavailable, using this device', err);
    }
    return Date.now();
  };
  const check = async () => {
    clearTimeout(timer);
    const wait = at - (await storeNow());
    if (wait <= 0) timer = setTimeout(reopen, tries ? Math.min(1500 * 2 ** tries, 15000) : 0);
    else if (wait > 90000) timer = setTimeout(check, Math.min(wait - 60000, 2147483000));
    else timer = setTimeout(check, wait + 1500);
  };
  check();
  document.addEventListener('visibilitychange', () => { if (!document.hidden) check(); });
  window.addEventListener('pageshow', (e) => { if (e.persisted) check(); });
}

// The announcement line's height, so a full-screen hero fills exactly what is left of the first screen.
function measureAnnouncement() {
  const bar = document.querySelector('.announcement');
  const set = () => document.documentElement.style.setProperty('--announce-h', `${bar ? bar.offsetHeight : 0}px`);
  set();
  if (bar && 'ResizeObserver' in window) new ResizeObserver(set).observe(bar);
}

// A hero video can be paused; it never plays for people who ask for less motion.
function initHeroVideo() {
  document.querySelectorAll('.hero').forEach((hero) => {
    const video = hero.querySelector('video');
    const btn = hero.querySelector('[data-hero-pause]');
    if (!video || !btn) return;
    const label = btn.querySelector('[data-hero-pause-label]');
    const sync = () => {
      btn.setAttribute('aria-pressed', String(video.paused));
      if (label) label.textContent = video.paused ? label.dataset.play : label.dataset.pause;
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { video.removeAttribute('autoplay'); video.pause(); }
    btn.addEventListener('click', () => { if (video.paused) video.play(); else video.pause(); });
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    sync();
  });
}

/* Add-on hour: the order confirmation email links to /discount/ADDON with the shop as the redirect and
   ?addon=1 on it. That visit marks the browser for an hour (localStorage, so a new tab from the email counts),
   and while it lasts the page carries .is-addon: the bar under the header and the bag note say shipping is
   free with the same email, and the free shipping progress bar steps aside (v13.css). The discount itself is
   limited in Shopify to the customers Flow tags for an hour after an order, so this only explains it. */
const ADDON_UNTIL = 'flm-addon-until';
const ADDON_HOUR = 60 * 60 * 1000;
function initAddon() {
  if (!document.querySelector('[data-addon-bar]')) return;
  const arrived = new URLSearchParams(window.location.search).get('addon') === '1';
  let until = arrived ? Date.now() + ADDON_HOUR : 0;
  try {
    if (arrived) localStorage.setItem(ADDON_UNTIL, String(until));
    else until = Number(localStorage.getItem(ADDON_UNTIL)) || 0;
  } catch (err) { /* storage blocked: the note shows on this page only */ }
  document.documentElement.classList.toggle('is-addon', until > Date.now());
}

/* This is a gift (snippets/cart-gift): the box saves the Gift order attribute and the message saves as the
   order note, through /cart/update.js as they change, so nothing is lost when the drawer re-renders. Unticking
   clears both. Without script the form that holds them posts them with Check out. */
async function saveGift(root, body) {
  const saved = root.querySelector('[data-gift-saved]');
  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`cart/update ${res.status}`);
    if (saved) saved.textContent = 'Saved';
  } catch (err) {
    console.error('[gift]', err);
    if (saved) saved.textContent = 'Not saved. It will be kept when you press Check out.';
  }
}
let giftTimer = 0;
document.addEventListener('change', (e) => {
  const box = e.target.closest('[data-gift-box]');
  if (!box) return;
  const root = box.closest('[data-cart-gift]');
  root.classList.toggle('is-on', box.checked);
  const note = root.querySelector('[data-gift-note]');
  if (!box.checked && note) note.value = '';
  saveGift(root, { attributes: { Gift: box.checked ? 'Yes' : '' }, note: box.checked && note ? note.value : '' });
  if (box.checked && note) note.focus();
});
document.addEventListener('input', (e) => {
  const note = e.target.closest('[data-gift-note]');
  if (!note) return;
  const root = note.closest('[data-cart-gift]');
  const saved = root.querySelector('[data-gift-saved]');
  if (saved) saved.textContent = '';
  clearTimeout(giftTimer);
  giftTimer = setTimeout(() => saveGift(root, { note: note.value }), 600);
});

function init() {
  // An image that finished before the head listener existed (bfcache, a fast cache hit) still shows.
  document.querySelectorAll('img[loading="lazy"]').forEach((img) => { if (img.complete) img.classList.add('is-loaded'); });
  initProduct();
  initPick();
  initForms();
  initTexts();
  initCookies();
  initOpening();
  measureAnnouncement();
  initHeroVideo();
  initAddon();
}
document.addEventListener('shopify:section:load', (e) => { initPick(e.target); initForms(e.target); initTexts(e.target); });
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

/* Shop Filter and Sort: one panel open at a time, Escape or a click outside
   closes it and returns focus to its button. Without this script both still
   open and close as plain disclosures. */
(() => {
  const menus = [...document.querySelectorAll('[data-tools] details')];
  if (!menus.length) return;
  menus.forEach((d) => d.addEventListener('toggle', () => {
    if (d.open) menus.forEach((o) => { if (o !== d) o.open = false; });
  }));
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const d = menus.find((m) => m.open);
    if (!d) return;
    d.open = false;
    d.querySelector('summary').focus();
  });
  document.addEventListener('click', (e) => {
    menus.forEach((d) => { if (d.open && !d.contains(e.target)) d.open = false; });
  });
})();

/* Recommendations: ask Shopify for this product's suggestions once the section
   is near the screen, then put them at the front of the row the page rendered
   (one product from each other shelf), so the row stays full when Shopify only
   has one or two. A failure leaves the page's row as it is. */
class ProductRecs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded) return;
    const load = async () => {
      this.dataset.loaded = '1';
      try {
        const res = await fetch(this.dataset.url);
        if (!res.ok) throw new Error(`recommendations ${res.status}`);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = html.querySelector('product-recs');
        const freshGrid = fresh?.querySelector('.recs__grid');
        if (freshGrid) {
          const count = Number(this.dataset.count) || 4;
          const have = new Set([...freshGrid.children].map((li) => li.dataset.recsHandle));
          const fill = [...this.querySelectorAll('.recs__grid > li')].filter((li) => !have.has(li.dataset.recsHandle));
          fill.slice(0, Math.max(0, count - freshGrid.children.length)).forEach((li) => freshGrid.append(li));
          this.innerHTML = fresh.innerHTML;
          // The page just grew: let the buy bar and header recheck where they are.
          window.dispatchEvent(new Event('resize'));
        }
      } catch (err) {
        console.warn('[recommendations]', err);
      }
    };
    if (!('IntersectionObserver' in window)) return void load();
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); load(); }
    }, { rootMargin: '600px 0px' });
    io.observe(this);
  }
}
if (!customElements.get('product-recs')) customElements.define('product-recs', ProductRecs);

/* Build your bundle (snippets/bundle-builder). A pick names itself beside its
   step, fills its tile in the strip, and puts its photograph, name, price and
   description on the stage; the home bundle section's big photograph follows
   too. A strip tile shows that piece again. The radios are required, so the
   browser asks for a missing pick; the message under the steps says it in
   words and scrolls the first open step into view. */
function initBuild(root = document) {
  // Build your bundle: the steps fold like an accordion inside the add form;
  // the board (the set as three photographs, the price, how many picks are
  // left, the Add button) sits in the other column under the same id.
  root.querySelectorAll('[data-build]').forEach((build) => {
    const form = build.closest('form');
    const board = document.querySelector(`[data-build-preview="${build.dataset.build}"]`);
    const steps = [...build.querySelectorAll('[data-build-step]')];
    const picks = steps.filter((s) => s.dataset.buildStep !== 'review');
    const review = steps.find((s) => s.dataset.buildStep === 'review');
    const need = build.querySelector('[data-build-need]');
    const status = board?.querySelector('[data-build-status]');
    const motion = () => (reduceMotion.matches ? 'auto' : 'smooth');
    build.classList.add('build--js');

    // Mats: one material at a time behind tabs, so a phone sees a short grid.
    // Opens on the material of the mat already picked, else the first tab.
    build.querySelectorAll('[data-build-filter]').forEach((filter) => {
      const step = filter.closest('[data-build-step]');
      const tabs = [...filter.querySelectorAll('[data-build-show]')];
      if (!step || tabs.length < 2) return;
      const more = step.querySelector('[data-build-more]');
      const phone = window.matchMedia('(max-width: 599px)');
      const PHONE_CAP = 8; // four rows of two
      let open = false;
      const clip = () => {
        const cells = [...step.querySelectorAll('[data-build-cell]')].filter((c) => !c.hidden);
        const pickedAt = cells.findIndex((c) => c.querySelector('input:checked'));
        const cap = !open && phone.matches && cells.length > PHONE_CAP + 2 && pickedAt < PHONE_CAP;
        cells.forEach((c, i) => c.classList.toggle('is-clipped', cap && i >= PHONE_CAP));
        if (!more) return;
        more.hidden = !cap;
        more.textContent = (more.dataset.template || '').replace('[n]', String(cells.length));
      };
      const show = (material) => {
        tabs.forEach((t) => t.setAttribute('aria-pressed', String(t.dataset.buildShow === material)));
        step.querySelectorAll('[data-build-cell]').forEach((c) => { c.hidden = c.dataset.buildCell !== 'other' && c.dataset.buildCell !== material; });
        step.querySelectorAll('[data-build-group]').forEach((g) => { g.hidden = true; });
        open = false;
        clip();
      };
      more?.addEventListener('click', () => {
        const first = step.querySelector('[data-build-cell].is-clipped input');
        open = true;
        clip();
        first?.focus();
      });
      phone.addEventListener?.('change', clip);
      tabs.forEach((t) => t.addEventListener('click', () => show(t.dataset.buildShow)));
      const picked = step.querySelector('input:checked')?.dataset.buildMaterial;
      show(tabs.some((t) => t.dataset.buildShow === picked) ? picked : tabs[0].dataset.buildShow);
      filter.hidden = false;
      step.classList.add('has-filter');
    });

    // On one column (phones, tablets) the board moves from its column to the
    // top of the steps as a tray that stays under the header while the steps
    // scroll (sticky only holds inside its own parent).
    // The inline script after the steps markup already moved the board on a
    // narrow screen before first paint and marked where it came from, so the
    // page does not shift once the script runs.
    const home = document.querySelector(`[data-build-home="${build.dataset.build}"]`) || board?.parentElement;
    const narrow = window.matchMedia('(max-width: 999px)');
    const placeBoard = () => {
      if (!board || !home) return;
      if (narrow.matches) { if (board.parentElement !== build) build.prepend(board); }
      else if (board.parentElement !== home) home.append(board);
    };
    placeBoard();
    narrow.addEventListener('change', placeBoard);

    // A photograph into a slot (a blank span the first time, an img after);
    // the old one stays until the new one has decoded, so nothing goes blank.
    const setImg = (slot, src, srcset, sizes, big) => {
      if (!slot || !src) return;
      if (slot.tagName === 'IMG') {
        if (slot.dataset.showing === src) return;
        slot.dataset.showing = src;
        const next = new Image();
        if (srcset) next.srcset = srcset;
        if (sizes) next.sizes = sizes;
        next.src = src;
        const swap = () => { if (slot.dataset.showing !== src) return; if (srcset) slot.srcset = srcset; slot.src = src; };
        (next.decode ? next.decode() : Promise.resolve()).then(swap, swap);
        return;
      }
      const img = document.createElement('img');
      img.src = src;
      if (srcset) img.srcset = srcset;
      if (sizes) img.sizes = sizes;
      img.alt = '';
      img.width = big ? 800 : 56;
      img.height = big ? 800 : 56;
      img.dataset.showing = src;
      [...slot.attributes].forEach((a) => { if (a.name.startsWith('data-')) img.setAttribute(a.name, a.value); });
      slot.replaceWith(img);
    };

    const setOpen = (step, on) => {
      const head = step.querySelector('[data-build-head]');
      const body = step.querySelector('[data-build-body]');
      if (!head || !body) return;
      head.setAttribute('aria-expanded', String(on));
      body.hidden = !on;
      step.classList.toggle('is-open', on);
    };
    // On one column the board is a sticky tray over the steps, so a step lands
    // under the tray, not just under the header. Scrolling up brings the header
    // back above the tray; scrolling down hides it.
    const reveal = (step) => {
      if (!narrow.matches || !board) { step.scrollIntoView({ block: 'start', behavior: motion() }); return; }
      const top = step.getBoundingClientRect().top;
      const header = top < 0 ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 0 : 0;
      window.scrollTo({ top: window.scrollY + top - board.offsetHeight - header - 12, behavior: motion() });
    };
    const goTo = (step, focusPick) => {
      if (!step) return;
      setOpen(step, true);
      reveal(step);
      if (focusPick) (step.querySelector('.build__input:checked') || step.querySelector('.build__input:not(:disabled)'))?.focus({ preventScroll: true });
    };
    const nextOpen = (from) => {
      const after = steps.slice(steps.indexOf(from) + 1);
      return after.find((s) => s.dataset.buildStep !== 'review' && !s.querySelector('input:checked')) || picks.find((s) => !s.querySelector('input:checked')) || review;
    };

    // The bundle's variant: with a second "Leather" variant in Shopify, a
    // leatherette mat switches the form to it and every price on the page
    // follows, so Shopify charges the higher price. With one variant, nothing moves.
    let variantsJson = null;
    try { variantsJson = build.dataset.buildVariants ? JSON.parse(build.dataset.buildVariants) : null; } catch { variantsJson = null; }
    const applyVariant = () => {
      if (!variantsJson || !variantsJson.leather || !variantsJson.base) return;
      const mat = build.querySelector('[data-build-step="mat"] input:checked');
      const use = mat && mat.dataset.buildMaterial === 'leatherette' ? variantsJson.leather : variantsJson.base;
      const idInput = form?.querySelector('[data-variant-id]') || form?.querySelector('input[name="id"]');
      if (idInput) idInput.value = String(use.id);
      // The add button puts this price back after its "Added" moment.
      if (form) form.dataset.currentPrice = use.price;
      // Only this bundle's own prices: the product page, or the home section it sits in.
      const scope = build.closest('.pdp') || build.closest('.shopify-section') || document;
      scope.querySelectorAll('[data-price]').forEach((el) => (el.textContent = use.price));
      scope.querySelectorAll('[data-build-compare]').forEach((el) => { el.textContent = use.compare || ''; el.hidden = !use.compare; });
      scope.querySelectorAll('[data-build-save]').forEach((el) => { el.textContent = use.save || ''; el.hidden = !use.save; });
      [form?.querySelector('[data-main-add]') || form?.querySelector('button[type="submit"]'), board?.querySelector('[data-build-add]')].forEach((b) => { if (b && form?.dataset.held === 'open') b.disabled = !use.available; });
    };

    // The board's line: how many picks are left, or that the set is complete.
    const refresh = () => {
      const left = picks.filter((s) => !s.querySelector('input:checked')).length;
      const done = left === 0;
      build.classList.toggle('is-complete', done);
      board?.classList.toggle('is-complete', done);
      if (status) status.textContent = done ? status.dataset.ready : left === 1 ? status.dataset.one : (status.dataset.other || '').replace('%n', String(left));
      applyVariant();
    };

    const pick = (input, byHand) => {
      const step = input.closest('[data-build-step]');
      const key = step?.dataset.buildStep;
      const name = input.dataset.buildName || input.value;
      step?.classList.add('is-done');
      const picked = step?.querySelector('[data-build-picked]');
      if (picked) picked.textContent = name;
      // The hidden variant id the stock workflow reads (snippets/bundle-builder).
      const vidField = build.querySelector(`[data-build-vid-field="${key}"]`);
      if (vidField) vidField.value = input.dataset.buildVid || '';
      // The board: this part's slot gets the photograph and the name.
      const slot = board?.querySelector(`[data-build-slot="${key}"]`);
      if (slot) {
        slot.classList.remove('is-empty');
        const label = slot.querySelector('[data-build-slot-name]');
        if (label) label.textContent = name;
        setImg(slot.querySelector('[data-build-slot-img]'), input.dataset.buildImg, input.dataset.buildSrcset, key === 'tiles' ? '(min-width: 1000px) 46vw, 30vw' : '(min-width: 1000px) 23vw, 30vw', true);
      }
      // The review: the same part, with its Edit link.
      const sum = build.querySelector(`[data-build-sum="${key}"]`);
      if (sum) {
        const sumName = sum.querySelector('[data-build-sum-name]');
        if (sumName) sumName.textContent = name;
        setImg(sum.querySelector('[data-build-sum-img]'), input.dataset.buildThumb);
      }
      if (need) need.textContent = '';
      refresh();
      if (!byHand) return;
      // A pick folds its step after a beat (so the tick shows) and opens the
      // next step still waiting on one, or the review.
      const next = nextOpen(step);
      setTimeout(() => {
        setOpen(step, false);
        goTo(next, false);
      }, reduceMotion.matches ? 0 : 260);
    };

    build.addEventListener('change', (e) => {
      const input = e.target.closest('.build__input');
      if (input) pick(input, true);
    });
    build.querySelectorAll('[data-build-head]').forEach((head) => head.addEventListener('click', () => {
      const step = head.closest('[data-build-step]');
      const open = head.getAttribute('aria-expanded') === 'true';
      setOpen(step, !open);
      if (!open) reveal(step);
    }));
    // Edit in the review, or a slot on the board: back to that step, focus on its pick.
    const edits = [...build.querySelectorAll('[data-build-edit]'), ...(board ? [...board.querySelectorAll('[data-build-go]')] : [])];
    edits.forEach((a) => a.addEventListener('click', (e) => {
      const key = a.dataset.buildEdit || a.dataset.buildGo;
      const step = steps.find((s) => s.dataset.buildStep === key);
      if (!step) return;
      e.preventDefault();
      goTo(step, true);
    }));
    // Picks already made (the tiles, or a form the browser restored) fill the
    // board and the summary; done steps fold, the first open one waits.
    const sync = () => {
      build.querySelectorAll('.build__input:checked').forEach((input) => pick(input, false));
      let opened = false;
      picks.forEach((s) => {
        const todo = !s.querySelector('input:checked');
        setOpen(s, todo && !opened);
        if (todo) opened = true;
      });
      refresh();
    };
    sync();

    // A missing pick: the form's own message replaces the browser bubble, the
    // first missing step opens and scrolls into view, its first option takes focus.
    let invalidTick = false;
    form?.addEventListener('invalid', (e) => {
      const input = e.target.closest('.build__input');
      if (!input) return;
      e.preventDefault();
      if (invalidTick) return;
      invalidTick = true;
      setTimeout(() => { invalidTick = false; }, 0);
      if (need) need.textContent = need.dataset.text || '';
      goTo(input.closest('[data-build-step]'), true);
    }, true);
    // Back or forward can bring the page back with the browser's restored
    // picks after this ran; sync the board and summary with the form again.
    window.addEventListener('pageshow', sync);
  });
}

function initPredictive(root = document) {
  // Predictive search under any search field: Shopify's /search/suggest.json,
  // six products, arrow keys to move through them, Escape to close.
  const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const money = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return esc(v);
    try {
      const code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: code, maximumFractionDigits: Number.isInteger(n) ? 0 : 2 }).format(n);
    } catch { return esc(v); }
  };
  const sized = (u, w) => (u ? u + (u.includes('?') ? '&' : '?') + 'width=' + w : '');
  root.querySelectorAll('form[role="search"][data-suggest-label]').forEach((form) => {
    const input = form.querySelector('input[type="search"]');
    let box = form.querySelector('[data-suggest]');
    if (!input) return;
    if (!box) {
      box = document.createElement('div');
      box.className = 'suggest';
      box.hidden = true;
      box.setAttribute('data-suggest', '');
      form.append(box);
    }
    input.setAttribute('aria-expanded', 'false');
    let timer;
    let ctrl;
    const close = () => { box.hidden = true; box.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); };
    const run = async () => {
      const q = input.value.trim();
      if (q.length < 2) return close();
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      try {
        const r = await fetch(`${form.action}/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
        if (!r.ok) return close();
        const data = await r.json();
        const items = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (!items.length) return close();
        box.innerHTML = `<p class="eyebrow suggest__title">${esc(form.dataset.suggestLabel)}</p><ul class="suggest__list" role="list">${items.map((p) => `<li><a class="suggest__item" href="${esc(p.url)}">${p.featured_image && p.featured_image.url ? `<img src="${esc(sized(p.featured_image.url, 96))}" alt="" width="48" height="60" loading="lazy">` : '<span class="suggest__blank" aria-hidden="true"></span>'}<span class="suggest__name t-m">${esc(p.title)}</span><span class="small muted tabular">${p.available === false ? esc(form.dataset.suggestSold || '') : money(p.price)}</span></a></li>`).join('')}</ul><a class="link small suggest__all" href="${esc(form.action)}?q=${encodeURIComponent(q)}">${esc(form.dataset.suggestAll || '')}</a>`;
        box.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      } catch (err) {
        if (err.name !== 'AbortError') close();
      }
    };
    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(run, 180); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowDown' && !box.hidden) { e.preventDefault(); const a = box.querySelector('a'); if (a) a.focus(); }
    });
    box.addEventListener('keydown', (e) => {
      const links = [...box.querySelectorAll('a')];
      const i = links.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); (links[i + 1] || links[0]).focus(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); if (i <= 0) input.focus(); else links[i - 1].focus(); }
      if (e.key === 'Escape') { close(); input.focus(); }
    });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) close(); });
  });
}

initPredictive();
document.addEventListener('shopify:section:load', (e) => initPredictive(e.target));
initBuild();
document.addEventListener('shopify:section:load', (e) => initBuild(e.target));

/* Product rails: the arrows move one card width and switch off at either end. */
document.querySelectorAll('[data-rail]').forEach((rail) => {
  const track = rail.querySelector('[data-rail-track]');
  const prev = rail.querySelector('[data-rail-prev]');
  const next = rail.querySelector('[data-rail-next]');
  if (!track || !prev || !next) return;
  const step = () => (track.querySelector('li')?.getBoundingClientRect().width || track.clientWidth / 2) + parseFloat(getComputedStyle(track).columnGap || 0);
  const nav = prev.parentElement;
  const sync = () => {
    // With every card in view there is nothing to move: hide the arrows.
    if (nav) nav.hidden = track.scrollWidth <= track.clientWidth + 2;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  };
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduce ? 'auto' : 'smooth' }));
  next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduce ? 'auto' : 'smooth' }));
  track.addEventListener('scroll', sync, { passive: true });
  window.addEventListener('resize', sync);
  sync();
});
